import React, {useEffect, useRef, useState} from 'react';
import {useSelector} from "react-redux";
import { PowerBIEmbed } from 'powerbi-client-react';
import * as pbi from 'powerbi-client';
import axios from "axios";
import '../../../styles/PBI.css';
import '../../../styles/PBI.css';

import useMainCanvasSize from "../../../hooks/useMainCanvasSize";

import useWindowSize from "../../../hooks/useWindowSize";
import useDataSource from "../../../hooks/useDataSource";
import { styled } from '@mui/material';

const PREFIX = 'PBIEmbed';

const classes = {
    root: `${PREFIX}-root`,
    linearProgress: `${PREFIX}-linearProgress`
};

const Root = styled('div')((
    {
        theme
    }
) => ({
    [`&.${classes.root}`]: {
        height: 'calc(100% - 1px)'
    },

    [`& .${classes.linearProgress}`]: {
        height: '1px'
    }
}));

export default function PBIEmbed({configuration}) {


    const chartRef = useRef(null);
    const config = useSelector(state => state.config);
    const dataSources  = useSelector(state => state.data.dataSources);
    const filters = useSelector(state => state.data.filters);

    const pbiListeners = configuration.dataSource.listeners;
    const workspaceID = configuration.element.source.workspaceID;
    const objectID = configuration.element.source.objectID; // ReportID || DashboardID
    const type = configuration?.element?.type !== undefined ? configuration.element.type : 'report'; // report | dashboard | visual | tile
    const crossFilter = configuration?.element?.crossFilter !== undefined ? configuration.element.crossFilter : false;
    const tileID = configuration?.element?.tileID !== undefined ? configuration.element.tileID : undefined;
    const visualName = configuration?.element?.visualName !== undefined ? configuration.element.visualName : undefined;
    const defaultPage = configuration?.element?.defaultPage !== undefined ? configuration.element.defaultPage : undefined;
    let pageView = 'fitToWidth';
    if (configuration?.element?.pageView !== undefined) {
        if (configuration.element.pageView.toLowerCase() === 'fittowidth') {
            pageView = 'fitToWidth';
        } else if (configuration.element.pageView.toLowerCase() === 'onecolumn') {
            pageView = 'oneColumn';
        } else if (configuration.element.pageView.toLowerCase() === 'actualsize') {
            pageView = 'actualSize';
        }
    }
    const settings = configuration?.element?.settings !== undefined ? configuration.element.settings : undefined;

    // Component State
    const [modifiedFiltersFlag, setModifiedFiltersFlag] = useState(false);
    const [initialFilters, setInitialFilters] = useState([]);
    const [embedObject, setEmbedObject] = useState(null);
    const [embedObjLoaded, setEmbedObjLoaded] = useState(false);
    const [embedObjRendered, setEmbedObjRendered] = useState(false);
    const [embedObjError, setEmbedObjError] = useState(false);
    const [activePage, setActivePage] = useState({page: undefined, name: defaultPage, mobileLayout: false});
    const [previousClientSize, setPreviousClientSize] = useState(null);

    const size = useMainCanvasSize();
    const windowSize = useWindowSize();

    // Object config useState hook
    // Values for properties like embedUrl, accessToken and settings will be set on click of buttons below
    // https://docs.microsoft.com/en-us/javascript/api/overview/powerbi/configure-report-settings
    const [objectConfig, setObjectConfig] = useState({
        accessToken: undefined,
        embedUrl: undefined,
        dashboardId: type !== 'tile' ? undefined : objectID,
        id: type !== 'tile' ? objectID : tileID,
        visualName: visualName,
        pageView: pageView, // fitToWidth | oneColumn | actualSize
        pageName: defaultPage,
        permissions: pbi.models.Permissions.Read,
        tokenType: pbi.models.TokenType.Embed,
        type: type,
        viewMode: pbi.models.ViewMode.View,
        settings: {
            // background: pbi.models.BackgroundType.Transparent,
            bars: {
                actionBar: {
                    visible: false
                }
            },
            layoutType: pbi.models.LayoutType.Master,
            // localeSettings: {
            //     language: "en",
            //     formatLocale: "us"
            // },
            panes: {
                bookmarks: {
                    visible: false
                },
                fields: {
                    expanded: false
                },
                filters: {
                    expanded: false,
                    visible: false
                },
                pageNavigation: {
                    visible: settings?.pageNavigationVisible !== undefined ? settings.pageNavigationVisible : false,
                    position: pbi.models.PageNavigationPosition.Bottom
                },
                selection: {
                    visible: false
                },
                syncSlicers: {
                    visible: true
                },
                visualizations: {
                    expanded: false
                }
            }
        }
    });

    //const [showProgress, setShowProgress] = useState(configuration.element?.showFetching !== undefined ? configuration.element?.showFetching : true);

    // Power BI REST API call
    useEffect(() => {
        const getRequest = "{0}pbi".format(config.data.authentication.api);
        axios.get(getRequest, {
                params: {
                    db: config.data.authentication.apiSource,
                    workspaceID: workspaceID,
                    objectID: objectID,
                    type: type, // report | dashboard | visual | tile
                    tileID: tileID !== undefined ? tileID : null
                }
            })
            .then(function (response) {
                // console.log('-----> PBIEmbed:useEffect(): ', response);

                const objects = response.data.EmbedObject;
                const embedChartObject = objects.find(u => u.ObjectId === objectID);

                // Set the fetched embedUrl and embedToken in the object config
                setObjectConfig({
                    ...objectConfig,
                    embedUrl: embedChartObject.EmbedUrl,
                    accessToken: response.data.EmbedToken.Token
                });
            })
            .catch(function (error) {
                console.log('-----> PBIEmbed:useEffect(): Error: ', error);
                console.error('Failed to fetch config for PBI object.');
            });
    },[])

    // When App Filters Change or after PBI is Loaded and Initial Filters found
    useEffect(() => {
        if (embedObject) {
            processFilters();
        }
    }, [filters, initialFilters]);

    useEffect( () => {
        // console.log('Is Report LOADED???');
        if (embedObjLoaded) {
            setPreviousClientSize({height: chartRef.current.clientHeight, width: chartRef.current.clientWidth});

            // Get Initial Filters from PBI
            embedObject.getFilters()
                .then(filters => {
                    //console.log('filters found: ', filters);
                    setInitialFilters(filters);
                })
                .catch(errors => {
                    //console.log('filters not found errors: ', errors);
                    processFilters();
                });
        }
    },[embedObjLoaded]);

    // useEffect( () => {
    //     if (embedObjRendered) {
    //         console.log("Report RENDERED");
    //     }
    // },[embedObjRendered]);

    // Screen resizing
    // useEffect( () => {
    //     if (embedObjLoaded) {
    //         console.log('size: ', size);
    //         console.log('windowSize: ', windowSize);
    //
    //         const clientHeight = chartRef.current.clientHeight;
    //         const clientWidth = chartRef.current.clientWidth;
    //
    //         // windowSize is just the entire window
    //         // size is the entire window and set on load, but only updates when the drawers open
    //         if (windowSize.width <= 768 || size.width <= 768) {
    //             const newSettings = {
    //                 layoutType: activePage.mobileLayout ? pbi.models.LayoutType.MobilePortrait : pbi.models.LayoutType.Master
    //             };
    //
    //             embedObject.updateSettings(newSettings)
    //                 .then(response => {
    //                         console.log(response);
    //                 })
    //                 .catch(errors => {
    //                         console.log(errors);
    //                 })
    //         } else if (windowSize.width > 768 || size.width > 768) {
    //             const newSettings = {
    //                 layoutType: pbi.models.LayoutType.Master
    //             };
    //
    //             embedObject.updateSettings(newSettings)
    //                 .then(response => {
    //                     console.log(response);
    //                 })
    //                 .catch(errors => {
    //                     console.log(errors);
    //                 })
    //         }
    //     }
    // }, [size, windowSize])

    const processFilters = () => {
        const appFilters = filters.filter(f => {return f.id !== "-2112"});
        let newFilters = [];

        // For Each AppFilter...
        for (let a = 0; a < appFilters.length; a++) {

            // ...loop through every PBI Listener...
            let pbiListenerSources = [];
            let pbiListenerFields = [];
            for (let i = 0; i < pbiListeners.length; i++) {
                let dataSource = dataSources.find(s => s.name.toLowerCase().trim() === pbiListeners[i].name.toLowerCase().trim());

                // Has a Filter already been created with the Listeners respective DataSource Table?  (-1 === NO | 0 === YES)
                const dsIndex = pbiListenerSources.findIndex((x) => {return x.toLowerCase() === dataSource.source.toLowerCase()});

                const fields = pbiListeners[i].fields;

                // ...and each of the PBI Listener's fields...
                for (let f = 0; f < fields.length; f++) {

                    // Has a Filter already been created with the Listeners respective DataSource Column?  (-1 === NO | 0 === YES)
                    const fIndex = pbiListenerFields.findIndex((x) => {return x.toLowerCase() === fields[f]});

                    // If NO Filter with respective TABLE OR NO Filter with respective COLUMN, then proceed!
                    if (dsIndex === -1 || fIndex === -1) {

                        // ...and if a PBI Listener Field === an App Filter Field, then create new PBI Filter!
                        if (fields[f].toLowerCase() === appFilters[a].field.toLowerCase()) {
                            newFilters.push(createPBIFilters(dataSource.source, fields[f].toLowerCase(), appFilters[a].conditions));

                            // Track the Sources and Fields to create a unique set of Filters
                            pbiListenerSources.push(dataSource.source);
                            pbiListenerFields.push(fields[f].toLowerCase());
                        }
                    }
                }
            }
        }

        console.log('New Filters: ', newFilters);

        // If there are New Filters, add them!
        if (newFilters.length > 0 && modifiedFiltersFlag === false) {
            embedObject.updateFilters(pbi.models.FiltersOperations.Add, newFilters)
                .then(filters => {
                    // console.log('filters set: ', filters);
                    setModifiedFiltersFlag(true);
                })
                .catch(errors => {
                    // console.log('filters errors: ', errors);
                });

        //  If Filters have already been modified, ReplaceAll using the initialFilters found on PBI
        } else if (modifiedFiltersFlag === true) {
            const allFilters = [...initialFilters, ...newFilters];
            embedObject.updateFilters(pbi.models.FiltersOperations.ReplaceAll, allFilters)
                .then(filters => {
                    // console.log('filters replaced: ', filters);
                })
                .catch(errors => {
                    // console.log('filters errors: ', errors);
                });
        }
    }

    const createPBIFilters = (table, field, conditions) => {
        let filterConditions;
        let value;

        if (conditions[0].trim().toLowerCase().startsWith("eq")) {
            value = conditions[0].trim().substr(3, conditions[0].trim().length);
            let newValue = value.replace(/[|&;$%@"'<>()+,]/g, "");
            filterConditions = {operator: "In", values: newValue};
        } else if (conditions[0].trim().toLowerCase().startsWith("ne")) {
            value = conditions[0].trim().substr(3, conditions[0].trim().length);
            let newValue = value.replace(/[|&;$%@"'<>()+,]/g, "");
            filterConditions = {operator: "NotIn", values: newValue};
        } else if (conditions[0].trim().toLowerCase().startsWith("gt")) {
            value = conditions[0].trim().substr(3, conditions[0].trim().length);
            let newValue = value.replace(/[|&;$%@"'<>()+,]/g, "");
            filterConditions = {operator: "In", values: newValue};
        } else if (conditions[0].trim().toLowerCase().startsWith("lt")) {
            value = conditions[0].trim().substr(3, conditions[0].trim().length);
            let newValue = value.replace(/[|&;$%@"'<>()+,]/g, "");
            filterConditions = {operator: "In", values: newValue};
        } else if (conditions[0].trim().toLowerCase().startsWith("gte")) {
            value = conditions[0].trim().substr(4, conditions[0].trim().length);
            let newValue = value.replace(/[|&;$%@"'<>()+,]/g, "");
            filterConditions = {operator: "In", values: newValue};
        } else if (conditions[0].trim().toLowerCase().startsWith("lte")) {
            value = conditions[0].trim().substr(4, conditions[0].trim().length);
            let newValue = value.replace(/[|&;$%@"'<>()+,]/g, "");
            filterConditions = {operator: "In", values: newValue};
        } else if (conditions[0].trim().toLowerCase().startsWith("like")) {
            value = conditions[0].trim().substr(4, conditions[0].trim().length);
            let newValue = value.replace(/[|&;$%@"'<>()+,]/g, "");
            filterConditions = {operator: "In", values: newValue};
        } else if (conditions[0].trim().toLowerCase().startsWith("inn")) {
            value = conditions[0].trim().substr(4, conditions[0].trim().length);
            let newValue = value.replace(/[|&;$%@"'<>()+]/g, "");   // NOTE: for INN, our filter condition may look like ['inn (19,42)'], so do NOT filter out commas!!!!
            filterConditions = {operator: "In", values: newValue};
        }

        // https://docs.microsoft.com/en-us/javascript/api/overview/powerbi/control-report-filters#basic-filter
        const basicFilter = pbi.models.BasicFilter = {
            $schema: "http://powerbi.com/product/schema#basic",
            target: {
                table: table,
                column: field
            },
            operator: filterConditions.operator,
            values: [filterConditions.values],
            filterType: pbi.models.FilterType.Basic,
            requireSingleSelection: false
        };
        console.log(basicFilter);
        // const advFilter = pbi.models.AdvancedFilter = {
        //     $schema: "http://powerbi.com/product/schema#advanced",
        //     target: {
        //         table: "Hierarchy", //table
        //         column: "WBSId"     //field
        //     },
        //     logicalOperator: "And", //filterConditions.length === 1 ? "And" : "Or",
        //     conditions: [
        //         {
        //             operator: "Contains",
        //             value: "Wash"
        //         }], //filterConditions,
        //     filterType: pbi.models.FilterType.Advanced
        // };
        // console.log(advFilter);

        // embedObject.updateFilters(pbi.models.FiltersOperations.Add, [basicFilter])
        //     .then(filters => {
        //         console.log('filters set: ', filters);
        //     })
        //     .catch(errors => {
        //         console.log('filters errors: ', errors);
        //     });
        return basicFilter;
    }

    // Get Report Pages
    // const getReportPages = () => {
    //     // console.log('getReportPages');
    //     embedObject.getPages()
    //         .then(function(pages) {
    //             console.log(pages);
    //             // Retrieve the page that contain the visual. For the sample report it will be the active page
    //             let page = pages.filter(function (page) {
    //                 return page.isActive
    //             })[0];
    //             console.log(page);
    //
    //             page.getVisuals()
    //                 .then(visual => {
    //                     console.log(visual);
    //                 })
    //                 .catch(error => {
    //                     console.log('**ERROR**', error);
    //                 })
    //
    //         })
    //         .catch(error => {
    //             console.log('**ERROR**', error);
    //         });
    // };

    const getActivePage = (entity) => {
        // if (embedObjRendered === true && type === 'report') {
        if (entity) {
            entity.getPages()
                .then(function (pages) {
                    // Retrieve the active page
                    let page = pages.filter(function (page) {
                        return page.isActive
                    })[0];
                    // console.log(page);
                    const pageName = page.name;

                    // Check if active page has mobile layout
                    page.hasLayout(pbi.models.LayoutType.MobilePortrait)
                        .then(function(mobile) {
                            //console.log('PBI Has Mobile Layout: ', mobile);
                            setActivePage({page: page, name: pageName, mobileLayout: mobile});
                        })
                        .catch(error => {
                            //console.log('**ERROR**', error);
                            setActivePage({page: page, name: pageName, mobileLayout: false});
                        });

                })
                .catch(error => {
                    //console.log('**ERROR**', error);
                });
        }
        // }
    };

    // Map of event handlers to be applied to the embedding object
    // https://docs.microsoft.com/en-us/javascript/api/overview/powerbi/handle-events
    const eventHandlersMap = new Map([
        ['loaded', function () {setEmbedObjLoaded(true);}],
        ['rendered', () => {setEmbedObjRendered(true);}],
        ['pageChanged', function (event, entity) {
            getActivePage(entity);
        }],
        ['error', function (event) {
            console.log(event.detail);
            setEmbedObjError(true);
        }]
    ]);

    return (
        <Root ref={chartRef} className={classes.root}>
            {/*{showProgress && dataSource.fetching &&*/}
            {/*    <LinearProgress className={classes.linearProgress}/>*/}
            {/*}*/}
            {/*{showProgress && !dataSource.fetching &&*/}
            {/*    <div className={classes.linearProgress}></div>*/}
            {/*}*/}

            {/* CSS is located in PBI.css file*/}
            <PowerBIEmbed
                embedConfig={objectConfig}
                eventHandlers={eventHandlersMap}
                cssClassName={"pbi"}
                getEmbeddedComponent={(embeddedObject) => {
                    // console.log(`Embedded object of type "${embeddedObject.embedtype}" received`);
                    setEmbedObject(embeddedObject);
                    window.report = embeddedObject;
                }}
            />

        </Root>
    );
}
